import { notifyModalOpen, notifyModalClose } from '../utils/modalState';
import React, { useState, useEffect } from 'react';
import { X, Wand2, Users, Target, BarChart3, UserCheck, UserX, Plus, Trash2 } from 'lucide-react';
import { INJURY_STATUS } from '../constants/injuries';
import { InjuryBadge } from './ui/InjuryBadge';
import type { InjuryStatus } from '../constants/injuries';
import { MobileSelect } from './ui/MobileSelect';
import {
  deserializeBatterySchedule,
  mergeBatterySchedulesIntoProtected,
  batteryConflictsWithDh,
  type BatteryScheduleRow
} from '../utils/batteryScheduleUtils';

interface RandomizationSettings {
  preserveColors: boolean;
  ensureFairPlayTime: boolean;
  showPreview: boolean;
  protectedPositions: { [inning: number]: { [position: string]: string } };
  standardProtectedPositions: { [position: string]: string }; // For non-baseball sports
  absentPlayerIds?: string[];
  /** Baseball fielding: same DH all innings */
  protectedDhPlayerId?: string;
  protectedPitchingSchedule?: BatteryScheduleRow[];
  protectedCatchingSchedule?: BatteryScheduleRow[];
}

interface RandomizationPreview {
  willRandomize: number;
  willBench: number;
  strategy: string;
}

function sortPlayersByJersey<T extends { number?: number }>(list: T[]): T[] {
  return [...list].sort((a, b) => (a.number ?? 0) - (b.number ?? 0));
}

function scheduleBlocksPreview(
  rows: BatteryScheduleRow[],
  totalInnings: number,
  playerLabel: (id: string) => string
): string[] {
  const lines: string[] = [];
  let inn = 1;
  for (const row of rows) {
    if (!row.playerId || row.innings < 1) continue;
    if (inn > totalInnings) break;
    const span = Math.min(row.innings, totalInnings - inn + 1);
    const end = inn + span - 1;
    lines.push(`Innings ${inn}–${end}: ${playerLabel(row.playerId)}`);
    inn = end + 1;
  }
  return lines;
}

function sumScheduledInnings(rows: BatteryScheduleRow[]): number {
  return rows.filter((r) => r.playerId).reduce((a, r) => a + Math.max(0, r.innings), 0);
}

interface RandomizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isRandomizing: boolean;
  settings: RandomizationSettings;
  onSettingsChange: (settings: Partial<RandomizationSettings>) => void;
  preview: RandomizationPreview | null;
  title: string;
  description: string;
  sport: string;
  activeTab: string;
  players: any[];
  positions: any[];
  teamInnings: number;
}

const RandomizationModal: React.FC<RandomizationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isRandomizing,
  settings,
  onSettingsChange,
  preview,
  title,
  description,
  sport,
  activeTab,
  players = [],
  positions = [],
  teamInnings
}) => {
  // Battery is always expanded by default for baseball/softball fielding so coaches
  // can immediately see and configure their pitchers and catchers.
  const [showProtectionSettings, setShowProtectionSettings] = useState(() => {
    return (sport === 'Baseball' || sport === 'Softball') && activeTab === 'Fielding';
  });
  const [pitchInningsInput, setPitchInningsInput] = useState<Record<number, string>>({});
  const [catchInningsInput, setCatchInningsInput] = useState<Record<number, string>>({});
  
  const isBaseballFielding = (sport === 'Baseball' || sport === 'Softball') && activeTab === 'Fielding';
  const isBaseballLineup = (sport === 'Baseball' || sport === 'Softball') && activeTab === 'Lineup';
  const isStandardSport = !isBaseballFielding && !isBaseballLineup;

  const syncInningsInputs = (pitchRows: BatteryScheduleRow[], catchRows: BatteryScheduleRow[]) => {
    setPitchInningsInput((prev) => {
      const next: Record<number, string> = {};
      for (let i = 0; i < pitchRows.length; i++) {
        next[i] = prev[i] ?? String(pitchRows[i]?.innings ?? 1);
      }
      return next;
    });
    setCatchInningsInput((prev) => {
      const next: Record<number, string> = {};
      for (let i = 0; i < catchRows.length; i++) {
        next[i] = prev[i] ?? String(catchRows[i]?.innings ?? 1);
      }
      return next;
    });
  };

  useEffect(() => {
    if (isOpen) {
      notifyModalOpen();
      return () => notifyModalClose();
    }
  }, [isOpen]);

  // Initialize protection settings and auto-mark "Out" players as absent when modal opens
  useEffect(() => {
    if (isOpen) {
      // Auto-mark players with "Out" injury status as absent
      const outPlayerIds = players
        .filter(player => player.injury_status === INJURY_STATUS.OUT)
        .map(player => player.id);
      
      const currentAbsent = settings.absentPlayerIds || [];
      const newAbsentIds = [...new Set([...currentAbsent, ...outPlayerIds])];
      
      if (isBaseballFielding && Object.keys(settings.protectedPositions).length === 0) {
        // Initialize empty protection for all innings
        const initialProtection: { [inning: number]: { [position: string]: string } } = {};
        for (let inning = 1; inning <= teamInnings; inning++) {
          initialProtection[inning] = {};
        }
        onSettingsChange({ 
          protectedPositions: initialProtection,
          protectedPitchingSchedule: [],
          protectedCatchingSchedule: [],
          absentPlayerIds: newAbsentIds
        });
      } else if (outPlayerIds.length > 0 && newAbsentIds.length !== currentAbsent.length) {
        // Only update if we have new "Out" players to add
        onSettingsChange({ absentPlayerIds: newAbsentIds });
      }
    }
  }, [isOpen, isBaseballFielding, teamInnings, settings.protectedPositions, settings.absentPlayerIds, players, onSettingsChange]);

  // Hydrate pitching/catching schedule rows from legacy per-inning P/C once per open
  useEffect(() => {
    if (!isOpen || !isBaseballFielding) return;
    const pitch = settings.protectedPitchingSchedule ?? [];
    const catchRows = settings.protectedCatchingSchedule ?? [];
    // If user has added any rows (even unassigned), do not overwrite their structure
    if (pitch.length > 0 || catchRows.length > 0) return;
    const pFrom = deserializeBatterySchedule(settings.protectedPositions, 'P', teamInnings);
    const cFrom = deserializeBatterySchedule(settings.protectedPositions, 'C', teamInnings);
    if (pFrom.length === 0 && cFrom.length === 0) return;
    onSettingsChange({
      protectedPitchingSchedule: pFrom,
      protectedCatchingSchedule: cFrom
    });
  }, [
    isOpen,
    isBaseballFielding,
    teamInnings,
    settings.protectedPositions,
    settings.protectedPitchingSchedule,
    settings.protectedCatchingSchedule,
    onSettingsChange
  ]);

  const commitBatterySchedules = (pitchRows: BatteryScheduleRow[], catchRows: BatteryScheduleRow[]) => {
    syncInningsInputs(pitchRows, catchRows);
    const merged = mergeBatterySchedulesIntoProtected(
      settings.protectedPositions,
      pitchRows,
      catchRows,
      teamInnings
    );
    let dh = settings.protectedDhPlayerId || '';
    if (dh && batteryConflictsWithDh(merged, dh, teamInnings)) {
      dh = '';
    }
    onSettingsChange({
      protectedPitchingSchedule: pitchRows,
      protectedCatchingSchedule: catchRows,
      protectedPositions: merged,
      protectedDhPlayerId: dh
    });
  };

  const updateProtectedDh = (playerId: string) => {
    if (playerId === '') {
      onSettingsChange({ protectedDhPlayerId: '' });
      return;
    }
    const nextPitch = (settings.protectedPitchingSchedule ?? []).filter((r) => r.playerId !== playerId);
    const nextCatch = (settings.protectedCatchingSchedule ?? []).filter((r) => r.playerId !== playerId);
    const merged = mergeBatterySchedulesIntoProtected(
      settings.protectedPositions,
      nextPitch,
      nextCatch,
      teamInnings
    );
    onSettingsChange({
      protectedDhPlayerId: playerId,
      protectedPitchingSchedule: nextPitch,
      protectedCatchingSchedule: nextCatch,
      protectedPositions: merged
    });
  };

  const getPlayersForBatterySelect = () =>
    sortPlayersByJersey(
      (players as any[]).filter((p) => !(settings.absentPlayerIds || []).includes(p.id))
    );

  const playerLabel = (id: string) => {
    const p = (players as any[]).find((x) => x.id === id);
    if (!p) return id;
    return `#${p.number} ${p.first_name} ${p.last_name}`;
  };

  const toggleAbsentPlayer = (playerId: string) => {
    const currentAbsent = settings.absentPlayerIds || [];
    const newAbsentIds = currentAbsent.includes(playerId)
      ? currentAbsent.filter(id => id !== playerId)
      : [...currentAbsent, playerId];
    
    // Remove from protected positions if marked as absent
    if (newAbsentIds.includes(playerId)) {
      const newProtectedPositions = { ...settings.protectedPositions };
      Object.keys(newProtectedPositions).forEach(inning => {
        Object.keys(newProtectedPositions[parseInt(inning)]).forEach(position => {
          if (newProtectedPositions[parseInt(inning)][position] === playerId) {
            delete newProtectedPositions[parseInt(inning)][position];
          }
        });
      });

      // Also remove from standard protected positions
      const newStandardProtectedPositions = { ...settings.standardProtectedPositions };
      Object.keys(newStandardProtectedPositions).forEach(position => {
        if (newStandardProtectedPositions[position] === playerId) {
          delete newStandardProtectedPositions[position];
        }
      });

      if (isBaseballFielding) {
        const nextPitch = (settings.protectedPitchingSchedule ?? []).filter((r) => r.playerId !== playerId);
        const nextCatch = (settings.protectedCatchingSchedule ?? []).filter((r) => r.playerId !== playerId);
        const mergedBattery = mergeBatterySchedulesIntoProtected(
          newProtectedPositions,
          nextPitch,
          nextCatch,
          teamInnings
        );
        onSettingsChange({
          absentPlayerIds: newAbsentIds,
          protectedPositions: mergedBattery,
          protectedPitchingSchedule: nextPitch,
          protectedCatchingSchedule: nextCatch,
          standardProtectedPositions: newStandardProtectedPositions,
          ...(settings.protectedDhPlayerId === playerId ? { protectedDhPlayerId: '' } : {})
        });
      } else {
        onSettingsChange({
          absentPlayerIds: newAbsentIds,
          protectedPositions: newProtectedPositions,
          standardProtectedPositions: newStandardProtectedPositions,
          ...(settings.protectedDhPlayerId === playerId ? { protectedDhPlayerId: '' } : {})
        });
      }
    } else {
      onSettingsChange({ absentPlayerIds: newAbsentIds });
    }
  };

  // Functions for standard sports (non-baseball)
  const updateStandardProtectedPosition = (position: string, playerId: string) => {
    const newProtectedPositions = { ...settings.standardProtectedPositions };
    
    if (playerId === '') {
      delete newProtectedPositions[position];
    } else {
      // Remove this player from any other position
      Object.keys(newProtectedPositions).forEach(pos => {
        if (newProtectedPositions[pos] === playerId && pos !== position) {
          delete newProtectedPositions[pos];
        }
      });
      newProtectedPositions[position] = playerId;
    }
    
    onSettingsChange({ standardProtectedPositions: newProtectedPositions });
  };

  const getStandardProtectedPlayerForPosition = (position: string): string => {
    return settings.standardProtectedPositions[position] || '';
  };

  const isPlayerStandardProtected = (playerId: string): boolean => {
    return Object.values(settings.standardProtectedPositions).includes(playerId);
  };

  const getAvailablePlayersForStandardPosition = (position: string): any[] => {
    const currentSelection = getStandardProtectedPlayerForPosition(position);
    const absentIds = settings.absentPlayerIds || [];
    
    return sortPlayersByJersey(
      players.filter(player => 
        !absentIds.includes(player.id) && // Not absent
        (player.id === currentSelection || !isPlayerStandardProtected(player.id))
      )
    );
  };

  if (!isOpen) return null;

  const pitchSchedule = settings.protectedPitchingSchedule ?? [];
  const catchSchedule = settings.protectedCatchingSchedule ?? [];
  const pitchSum = sumScheduledInnings(pitchSchedule);
  const catchSum = sumScheduledInnings(catchSchedule);
  const pitchPreview = scheduleBlocksPreview(pitchSchedule, teamInnings, playerLabel);
  const catchPreview = scheduleBlocksPreview(catchSchedule, teamInnings, playerLabel);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[9999] touch-manipulation"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0)',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      <div
        className="bg-white rounded-t-xl sm:rounded-xl shadow-xl max-w-4xl w-full animate-slide-up sm:animate-none flex flex-col overflow-hidden"
        style={{
          maxHeight: 'min(85vh, 85dvh)',
        }}
      >
        <div className="contents">
          {/* Header - Fixed */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-stone border-opacity-20 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="bg-primary bg-opacity-10 p-2 rounded-lg">
                <Wand2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-dark">{title}</h3>
                <p className="text-xs sm:text-sm text-gray-600">{description}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-cream rounded-lg transition-colors touch-manipulation"
              disabled={isRandomizing}
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Scrollable Content Area */}
          <div 
            className="flex-1 overflow-y-auto min-h-0 overscroll-contain -webkit-overflow-scrolling-touch"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {/* Field Builder CTA — baseball fielding only */}
            {isBaseballFielding && (
              <div className="mx-4 sm:mx-6 mt-4 sm:mt-5 mb-1 p-3 sm:p-4 bg-primary bg-opacity-5 border border-primary border-opacity-20 rounded-xl">
                <div className="flex items-start gap-3">
                  <Wand2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs sm:text-sm font-semibold text-primary">Let us build your lineup</p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Set your Battery below, then tap <strong>Build It</strong> — we'll place every player in their eligible positions, honor pitcher and catcher rest innings, and balance bench time automatically.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Baseball Fielding Protection Settings */}
            {isBaseballFielding && (
              <div className="p-4 sm:p-6 border-b border-stone border-opacity-20">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-medium text-dark flex items-center">
                    <UserCheck className="h-4 w-4 mr-2" />
                    Battery Setup
                    <span className="ml-1.5 text-xs font-normal text-gray-500">(optional)</span>
                  </h4>
                  <button
                    onClick={() => setShowProtectionSettings(!showProtectionSettings)}
                    className="text-primary hover:underline text-xs sm:text-sm"
                  >
                    {showProtectionSettings ? 'Hide' : 'Show'} Battery
                  </button>
                </div>
                
                {showProtectionSettings && (
                  <div className="space-y-4">
                    <p className="text-xs text-gray-600 mb-4">
                      Add pitchers and catchers in order. Each row is how many consecutive innings that player stays in the role
                      (for example 3 + 2 + 2 over seven innings covers innings 1–3, 4–5, and 6–7). Unlisted innings are still built out by player position.
                    </p>

                    {sport === 'Baseball' && (
                      <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Designated hitter (DH) — all innings
                        </label>
                        <p className="text-xs text-gray-600 mb-2">
                          Optional. The same player stays at DH for every inning (not shown on the diamond).
                        </p>
                        <MobileSelect
                          value={settings.protectedDhPlayerId || ''}
                          onChange={(v) => updateProtectedDh(v)}
                          options={[
                            { value: '', label: '-- No fixed DH --' },
                            ...sortPlayersByJersey(
                              (players as any[]).filter(
                                (p) => !(settings.absentPlayerIds || []).includes(p.id)
                              )
                            ).map((player) => ({
                              value: player.id,
                              label: `#${player.number} ${player.first_name} ${player.last_name}`,
                            })),
                          ]}
                          className="w-full max-w-md text-xs border border-gray-300 rounded px-2 py-1.5"
                          disabled={isRandomizing}
                          aria-label="Designated hitter"
                          sheetTitle="Select DH"
                        />
                      </div>
                    )}

                    {/* Pitching */}
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200 space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <h5 className="font-medium text-sm text-dark">Pitching</h5>
                        <button
                          type="button"
                          onClick={() =>
                            commitBatterySchedules([...pitchSchedule, { playerId: '', innings: 1 }], catchSchedule)
                          }
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                          disabled={isRandomizing}
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Add pitcher
                        </button>
                      </div>
                      <div className="space-y-2">
                        {pitchSchedule.length === 0 ? (
                          <p className="text-xs text-gray-500">No fixed pitchers — all innings use random pitcher.</p>
                        ) : (
                          pitchSchedule.map((row, idx) => (
                            <div
                              key={`p-${idx}`}
                              className="flex flex-wrap items-end gap-2 p-2 bg-white rounded border border-gray-100"
                            >
                              <div className="flex-1 min-w-[140px]">
                                <label className="block text-xs font-medium text-gray-700 mb-1">Pitcher</label>
                                <MobileSelect
                                  value={row.playerId}
                                  onChange={(v) => {
                                    const next = pitchSchedule.map((r, i) =>
                                      i === idx ? { ...r, playerId: v } : r
                                    );
                                    commitBatterySchedules(next, catchSchedule);
                                  }}
                                  options={[
                                    { value: '', label: '-- Select --' },
                                    ...getPlayersForBatterySelect().map((player) => ({
                                      value: player.id,
                                      label: `#${player.number} ${player.first_name} ${player.last_name}`,
                                    })),
                                  ]}
                                  className="w-full text-xs border border-gray-300 rounded px-2 py-1.5"
                                  disabled={isRandomizing}
                                  aria-label="Select pitcher"
                                  sheetTitle="Select Pitcher"
                                />
                              </div>
                              <div className="w-24">
                                <label className="block text-xs font-medium text-gray-700 mb-1">Innings</label>
                                <input
                                  type="number"
                                  min={1}
                                  max={99}
                                  inputMode="numeric"
                                  step={1}
                                  pattern="[0-9]*"
                                  value={pitchInningsInput[idx] ?? String(row.innings)}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    setPitchInningsInput((prev) => ({ ...prev, [idx]: raw }));
                                    if (raw.trim() === '') return;
                                    const parsed = Number.parseInt(raw, 10);
                                    if (!Number.isFinite(parsed)) return;
                                    const v = Math.max(1, parsed);
                                    const next = pitchSchedule.map((r, i) =>
                                      i === idx ? { ...r, innings: v } : r
                                    );
                                    commitBatterySchedules(next, catchSchedule);
                                  }}
                                  onBlur={() => {
                                    const raw = (pitchInningsInput[idx] ?? '').trim();
                                    const parsed = Number.parseInt(raw, 10);
                                    const v = Number.isFinite(parsed) ? Math.max(1, parsed) : 1;
                                    setPitchInningsInput((prev) => ({ ...prev, [idx]: String(v) }));
                                    const next = pitchSchedule.map((r, i) =>
                                      i === idx ? { ...r, innings: v } : r
                                    );
                                    commitBatterySchedules(next, catchSchedule);
                                  }}
                                  className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 touch-manipulation"
                                  disabled={isRandomizing}
                                  aria-label="Pitcher innings"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const next = pitchSchedule.filter((_, i) => i !== idx);
                                  commitBatterySchedules(next, catchSchedule);
                                }}
                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                disabled={isRandomizing}
                                aria-label="Remove pitcher row"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                      {pitchPreview.length > 0 && (
                        <ul className="text-xs text-gray-600 space-y-0.5 border-t border-gray-200 pt-2">
                          {pitchPreview.map((line, i) => (
                            <li key={`pp-${i}`}>{line}</li>
                          ))}
                        </ul>
                      )}
                      {pitchSum > teamInnings && (
                        <p className="text-xs text-amber-700">
                          Scheduled pitching spans {pitchSum} innings; only the first {teamInnings} apply for this game length.
                        </p>
                      )}
                      {pitchSum > 0 && pitchSum < teamInnings && (
                        <p className="text-xs text-gray-500">
                          Pitching is set for {pitchSum} of {teamInnings} innings; later innings use a random pitcher.
                        </p>
                      )}
                    </div>

                    {/* Catching */}
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200 space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <h5 className="font-medium text-sm text-dark">Catching</h5>
                        <button
                          type="button"
                          onClick={() =>
                            commitBatterySchedules(pitchSchedule, [...catchSchedule, { playerId: '', innings: 1 }])
                          }
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                          disabled={isRandomizing}
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Add catcher
                        </button>
                      </div>
                      <div className="space-y-2">
                        {catchSchedule.length === 0 ? (
                          <p className="text-xs text-gray-500">No fixed catchers — all innings use random catcher.</p>
                        ) : (
                          catchSchedule.map((row, idx) => (
                            <div
                              key={`c-${idx}`}
                              className="flex flex-wrap items-end gap-2 p-2 bg-white rounded border border-gray-100"
                            >
                              <div className="flex-1 min-w-[140px]">
                                <label className="block text-xs font-medium text-gray-700 mb-1">Catcher</label>
                                <MobileSelect
                                  value={row.playerId}
                                  onChange={(v) => {
                                    const next = catchSchedule.map((r, i) =>
                                      i === idx ? { ...r, playerId: v } : r
                                    );
                                    commitBatterySchedules(pitchSchedule, next);
                                  }}
                                  options={[
                                    { value: '', label: '-- Select --' },
                                    ...getPlayersForBatterySelect().map((player) => ({
                                      value: player.id,
                                      label: `#${player.number} ${player.first_name} ${player.last_name}`,
                                    })),
                                  ]}
                                  className="w-full text-xs border border-gray-300 rounded px-2 py-1.5"
                                  disabled={isRandomizing}
                                  aria-label="Select catcher"
                                  sheetTitle="Select Catcher"
                                />
                              </div>
                              <div className="w-24">
                                <label className="block text-xs font-medium text-gray-700 mb-1">Innings</label>
                                <input
                                  type="number"
                                  min={1}
                                  max={99}
                                  inputMode="numeric"
                                  step={1}
                                  pattern="[0-9]*"
                                  value={catchInningsInput[idx] ?? String(row.innings)}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    setCatchInningsInput((prev) => ({ ...prev, [idx]: raw }));
                                    if (raw.trim() === '') return;
                                    const parsed = Number.parseInt(raw, 10);
                                    if (!Number.isFinite(parsed)) return;
                                    const v = Math.max(1, parsed);
                                    const next = catchSchedule.map((r, i) =>
                                      i === idx ? { ...r, innings: v } : r
                                    );
                                    commitBatterySchedules(pitchSchedule, next);
                                  }}
                                  onBlur={() => {
                                    const raw = (catchInningsInput[idx] ?? '').trim();
                                    const parsed = Number.parseInt(raw, 10);
                                    const v = Number.isFinite(parsed) ? Math.max(1, parsed) : 1;
                                    setCatchInningsInput((prev) => ({ ...prev, [idx]: String(v) }));
                                    const next = catchSchedule.map((r, i) =>
                                      i === idx ? { ...r, innings: v } : r
                                    );
                                    commitBatterySchedules(pitchSchedule, next);
                                  }}
                                  className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 touch-manipulation"
                                  disabled={isRandomizing}
                                  aria-label="Catcher innings"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const next = catchSchedule.filter((_, i) => i !== idx);
                                  commitBatterySchedules(pitchSchedule, next);
                                }}
                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                disabled={isRandomizing}
                                aria-label="Remove catcher row"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                      {catchPreview.length > 0 && (
                        <ul className="text-xs text-gray-600 space-y-0.5 border-t border-gray-200 pt-2">
                          {catchPreview.map((line, i) => (
                            <li key={`cp-${i}`}>{line}</li>
                          ))}
                        </ul>
                      )}
                      {catchSum > teamInnings && (
                        <p className="text-xs text-amber-700">
                          Scheduled catching spans {catchSum} innings; only the first {teamInnings} apply for this game length.
                        </p>
                      )}
                      {catchSum > 0 && catchSum < teamInnings && (
                        <p className="text-xs text-gray-500">
                          Catching is set for {catchSum} of {teamInnings} innings; later innings use a random catcher.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Standard Sports Protection Settings */}
            {isStandardSport && (
              <div className="p-4 sm:p-6 border-b border-stone border-opacity-20">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-medium text-dark flex items-center">
                    <UserCheck className="h-4 w-4 mr-2" />
                    Protect Positions (Optional)
                  </h4>
                  <button
                    onClick={() => setShowProtectionSettings(!showProtectionSettings)}
                    className="text-primary hover:underline text-xs sm:text-sm"
                  >
                    {showProtectionSettings ? 'Hide' : 'Show'} Protected Positions
                  </button>
                </div>
                
                {showProtectionSettings && (
                  <div className="space-y-4">
                    <p className="text-xs text-gray-600 mb-4">
                      Set specific players for positions you want to protect from randomization. These positions will keep their assigned players while the rest are randomized.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 max-h-48 sm:max-h-60 overflow-y-auto">
                      {positions.map(position => (
                        <div key={position.key} className="bg-gray-50 rounded-lg p-2 sm:p-3">
                          <h5 className="font-medium text-xs sm:text-sm text-dark mb-2 sm:mb-3">
                            {position.fullName || position.name}
                          </h5>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Protected Player
                            </label>
                            <MobileSelect
                              value={getStandardProtectedPlayerForPosition(position.key)}
                              onChange={(v) => updateStandardProtectedPosition(position.key, v)}
                              options={[
                                { value: '', label: '-- Let randomize --' },
                                ...getAvailablePlayersForStandardPosition(position.key).map(player => ({
                                  value: player.id,
                                  label: `#${player.number} ${player.first_name} ${player.last_name}`,
                                })),
                              ]}
                              className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                              disabled={isRandomizing}
                              aria-label={`Protected player for ${position.key}`}
                              sheetTitle={`${position.key} — Protected Player`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Preview Section - Always shown */}
            {preview && (
              <div className="p-4 sm:p-6 border-b border-stone border-opacity-20">
                <h4 className="text-sm font-medium text-dark mb-3 flex items-center">
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Field Preview
                </h4>
                <div className="space-y-3">
                  <div className="bg-secondary-hover rounded-lg p-2 sm:p-3">
                    <div className="flex items-center justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">Strategy:</span>
                      <span className="font-medium text-dark">{preview.strategy}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="bg-green-50 rounded-lg p-2 sm:p-3 text-center">
                      <div className="flex items-center justify-center mb-1">
                        <Users className="h-4 w-4 text-green-600 mr-1" />
                      </div>
                      <div className="text-base sm:text-lg font-semibold text-green-700">{preview.willRandomize}</div>
                      <div className="text-xs text-green-600">Will Play</div>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-2 sm:p-3 text-center">
                      <div className="flex items-center justify-center mb-1">
                        <Target className="h-4 w-4 text-orange-600 mr-1" />
                      </div>
                      <div className="text-base sm:text-lg font-semibold text-orange-700">{preview.willBench}</div>
                      <div className="text-xs text-orange-600">On Bench</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Fair Play Time Setting (only for baseball fielding) */}
            {isBaseballFielding && (
              <div className="p-4 sm:p-6 border-b border-stone border-opacity-20">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-xs sm:text-sm font-medium text-dark">Enforce Bench Rules</label>
                    <p className="text-xs text-gray-600">No back-to-back bench time, max 2 innings on bench</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.ensureFairPlayTime}
                      onChange={(e) => onSettingsChange({ ensureFairPlayTime: e.target.checked })}
                      className="sr-only peer"
                      disabled={isRandomizing}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary peer-focus:ring-opacity-20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              </div>
            )}

            {/* Warning for Baseball Fielding */}
            {isBaseballFielding && (
              <div className="mx-4 sm:mx-6 mb-6 sm:mb-8 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <Target className="h-4 sm:h-5 w-4 sm:w-5 text-blue-600 mt-0.5" />
                  </div>
                  <div className="ml-2 sm:ml-3">
                    <h4 className="text-xs sm:text-sm font-medium text-blue-800">How the Field Builder works</h4>
                    <div className="text-xs text-blue-700 mt-1 space-y-1">
                      <p>• Players are only placed in positions listed on their player card</p>
                      <p>• <strong>Starting pitcher</strong> sits the inning <em>after</em> their last pitched inning</p>
                      <p>• <strong>Relief pitchers</strong> sit the inning <em>before</em> they take the mound</p>
                      <p>• <strong>Catchers</strong> sit the inning <em>after</em> their last catching inning</p>
                      <p>• No player sits two innings in a row</p>
                      <p>• Bench time is spread as evenly as possible across all players</p>
                      <p>• {preview?.willBench || 0} player{(preview?.willBench || 0) !== 1 ? 's' : ''} on bench each inning</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Warning for Lineup */}
            {isBaseballLineup && (
              <div className="mx-4 sm:mx-6 mb-6 sm:mb-8 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <Users className="h-4 sm:h-5 w-4 sm:w-5 text-green-600 mt-0.5" />
                  </div>
                  <div className="ml-2 sm:ml-3">
                    <h4 className="text-xs sm:text-sm font-medium text-green-800">Batting Order</h4>
                    <p className="text-xs text-green-700 mt-1">
                      This will randomly shuffle the batting order. Players not in the starting lineup will remain available as substitutes.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Warning for Standard Sports */}
            {isStandardSport && (
              <div className="mx-4 sm:mx-6 mb-6 sm:mb-8 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <UserCheck className="h-4 sm:h-5 w-4 sm:w-5 text-blue-600 mt-0.5" />
                  </div>
                  <div className="ml-2 sm:ml-3">
                    <h4 className="text-xs sm:text-sm font-medium text-blue-800">Protected Positions</h4>
                    <p className="text-xs text-blue-700 mt-1">
                      You can protect specific positions (like QB or C) from randomization. Protected players will stay in their assigned positions while the rest are randomized.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Unavailable Players Section */}
            <div className="p-4 sm:p-6 pt-8 sm:pt-10 border-t border-stone border-opacity-20">
              <h4 className="text-sm font-medium text-dark mb-3 flex items-center">
                <UserX className="h-4 w-4 mr-2 text-red-500" />
                Unavailable Players
              </h4>
              <p className="text-xs text-gray-600 mb-3 sm:mb-4">
                Select players who won't be at the game. They will be excluded from the field builder.
              </p>
              
              <div className="space-y-3 max-h-48 sm:max-h-64 overflow-y-auto">
                {players.map(player => {
                  const isOut = player.injury_status === INJURY_STATUS.OUT;
                  const isUnavailable = (settings.absentPlayerIds || []).includes(player.id);
                  return (
                    <label 
                      key={player.id} 
                      className={`flex items-center gap-2 p-2 sm:p-2.5 rounded hover:bg-gray-50 cursor-pointer ${
                        isOut ? 'bg-red-50 border border-red-200' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isUnavailable}
                        onChange={() => toggleAbsentPlayer(player.id)}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500 flex-shrink-0"
                        disabled={isRandomizing || isOut}
                      />
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <span className={`text-xs sm:text-sm truncate ${isOut ? 'text-red-700 font-medium' : ''}`}>
                          #{player.number} {player.first_name} {player.last_name}
                        </span>
                        {player.injury_status && player.injury_status !== 'H' && (
                          <InjuryBadge 
                            injuryStatus={player.injury_status as InjuryStatus} 
                            size="xs" 
                            className="flex-shrink-0"
                          />
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
              
              {(settings.absentPlayerIds || []).length > 0 && (
                <div className="mt-3 p-2 pl-6 sm:pr-12 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs text-red-700">
                    {(settings.absentPlayerIds || []).length} player(s) marked as unavailable
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons - Fixed at bottom */}
          <div className="flex space-x-3 p-4 sm:p-6 border-t border-stone border-opacity-20 flex-shrink-0 bg-white">
            <button
              onClick={onClose}
              className="flex-1 px-3 sm:px-4 py-3 sm:py-2 border border-stone border-opacity-20 rounded-xl text-dark hover:bg-cream transition-colors text-sm sm:text-base touch-manipulation min-h-[44px]"
              disabled={isRandomizing}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isRandomizing}
              className="flex-1 px-3 sm:px-4 py-3 sm:py-2 bg-primary text-white rounded-xl hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm sm:text-base touch-manipulation min-h-[44px]"
            >
              {isRandomizing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Building...
                </>
              ) : (
                <>
                  <Wand2 className="h-4 w-4 mr-2" />
                  Build It
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RandomizationModal; 